import { initializeApp } from 'firebase/app'
import type { User } from 'firebase/auth'
import { getAuth, onAuthStateChanged } from 'firebase/auth'
import { connectFirestoreEmulator, getFirestore } from 'firebase/firestore'
import { connectFunctionsEmulator, getFunctions } from 'firebase/functions'
import { userLogout } from '~/scripts/auth/applicationAuth'
import { initializeSnowflakeQueries } from '~/scripts/snowflake/initializeSnowflake'

// import { getAnalytics } from 'firebase/analytics'

export default defineNuxtPlugin((nuxtApp) => {
    const config = useRuntimeConfig()

    let firebaseConfig = config.public.firebaseConfig as any
    // If firebaseConfig is a string, parse it
    if (typeof firebaseConfig === 'string') firebaseConfig = JSON.parse(firebaseConfig)

    const app = initializeApp(firebaseConfig)

    // const analytics = getAnalytics(app);
    const auth = getAuth(app)
    const firestore = getFirestore(app)
    const functions = getFunctions(app)

    if (config.public.useFunctionEmulation) {
        console.log('Using Local Cloud Function Emulation on 127.0.0.1 Port 5001')
        connectFunctionsEmulator(functions, '127.0.0.1', 5001)
    }
    if (config.public.useFirestoreEmulation) {
        console.log('Using Local Firestore Emulation on 127.0.0.1 Port 8080')
        connectFirestoreEmulator(firestore, '127.0.0.1', 8080)
    }

    // //////////////////////////////////////////////////////////////////
    // Watches Authentication State
    // //////////////////////////////////////////////////////////////////
    const user = useState<User | null>('user', () => null)

    // 💬 Auth-hydration signal — lets the route middleware know when
    // it's safe to evaluate the accountId-claim gate.
    //
    // Why we need this: on a hard refresh the user cookie is restored
    // synchronously (so authStore.isLoggedIn === true), but Firebase's
    // onAuthStateChanged callback hasn't fired yet — so accountId is
    // still null. The middleware would see "logged in but no
    // accountId" and bounce the user to /register, which then
    // self-corrects but loses whatever URL they were trying to hit.
    //
    // Pattern: a single Promise resolved exactly once on the first
    // onAuthStateChanged callback. Middleware awaits this when a
    // cookie is present so the gate evaluates against fully-hydrated
    // state, not a half-initialized snapshot.
    let resolveAuthHydrated: () => void = () => {}
    const authHydrated = new Promise<void>((resolve) => {
      resolveAuthHydrated = resolve
    })

    // //////////////////////////////////////////////////////////////////
    // 💬 Watch Authentication & Data Initialization
    // //////////////////////////////////////////////////////////////////
    const userCookie = useCookie('user', { maxAge: 180 * 60 })

    // const { $auth } = useNuxtApp()
    const { syncAccountProfile, syncUserProfile, alertSnackbar } = useGlobalsStore()
    const authStore = useAuthStore()

    // Watches For Changes in User Authentication State.
    //
    // accountId is sourced from the user's custom claims (set by the
    // `app_register_account` Cloud Function during signup / migration). The
    // earlier displayName-as-accountId hack is gone — displayName is now
    // the user's actual name (Firebase's intended use), and accountId is
    // a JWT claim. That means Firestore security rules can enforce tenant
    // isolation via `request.auth.token.accountId == accountId` without
    // any get() calls — fast, safe, and unforgeable.
    //
    // Users who don't yet have an accountId claim (fresh signups, or
    // legacy users who pre-date this code) need to call /register
    // before they can do anything tenant-scoped. The auth middleware
    // routes them there.
    // 💬 Track whether we've seen a real user in this session.
    //
    // onAuthStateChanged can fire with `authUser=null` transiently
    // during signInWithCustomToken (Firebase clears the prior session
    // before assigning the new one). Without this guard, the auto-
    // logout below was triggering during legitimate sign-in flows —
    // most visible on fresh OAuth signups, where it kicked the user
    // straight from /register's submit back to /login mid-handshake.
    //
    // The flag flips to true ONLY when we see a non-null authUser.
    // The auto-logout check then requires it — so we never confuse
    // "transitioning between users" with "session was revoked."
    let sessionEverEstablished = false

    onAuthStateChanged(auth, async (authUser) => {
        authStore.setUser(authUser)
        console.log('Auth User:', authUser)

        // Checks Global accountId
        const { accountId, userId } = storeToRefs(useGlobalsStore())

        // If Authenticated and Data is Needed
        if (!accountId.value && authUser) {
            sessionEverEstablished = true
            userId.value = authUser.uid
            // Set Auth Cookie immediately — the rest of the app's
            // route guards key off this. Users without an account
            // claim still need cookie+auth to reach /register.
            userCookie.value = JSON.stringify({ uid: authUser.uid, email: authUser.email })

            // Pull accountId off the user's ID token claims.
            try {
                const tokenResult = await authUser.getIdTokenResult()
                const claimAccountId = (tokenResult.claims?.accountId as string | undefined) ?? null
                if (claimAccountId) {
                    accountId.value = claimAccountId
                    console.log('User Successfully Logged In', authUser)
                    initializeGlobalData(claimAccountId, authUser.uid)
                }
                else {
                    // No claim yet — middleware will redirect to /register.
                    console.warn('Authenticated user has no accountId claim — pending account setup.')
                }
            }
            catch (e) {
                console.error('Failed to read accountId claim from ID token:', e)
            }
        }
        else if (authUser) {
            // Already had accountId set; just confirm the session is
            // tracked as established so the logout guard below won't
            // fire on a transient null in this same listener cycle.
            sessionEverEstablished = true
        }

        // If Not Authenticated and Data Should be Cleared.
        //
        // Gated on `sessionEverEstablished` to avoid the spurious
        // logout described above. The check now means: "we WERE
        // authenticated in this tab and Firebase says we're not
        // anymore." That's a real revocation / explicit signOut /
        // session expiry — the kind of event the user should see
        // a snackbar about.
        if (sessionEverEstablished && accountId.value && !authUser) {
            console.warn('Snowflake Has Logged You Out. Please Log In Again.')
            alertSnackbar('Snowflake Has Logged You Out. Please Log In Again.', 'error', 5000)
            userLogout()
        }

        // Hydration done — middleware that's been awaiting this can
        // proceed. Resolve regardless of outcome (logged in, logged
        // out, or claim-less) — the middleware decides what to do
        // based on the resulting state, not on whether hydration
        // succeeded. Idempotent: Promise.resolve() after the first
        // is a no-op.
        resolveAuthHydrated()
    })

    // //////////////////////////////////////////////////////////////////
    // 💬 Data Initialization - If User is Authenticated
    // //////////////////////////////////////////////////////////////////
    function initializeGlobalData(accountId: string, uid: string) {
        // Subscribe to the Account and User Profile Docs From Firestore.
        // These power the global state (userProfile, accountProfile)
        // that the rest of the app reads via the globals store.
        syncAccountProfile(accountId)
        syncUserProfile(accountId, uid)

        // 🔐 Bind the permissions store so the user's role + permissions
        // are live alongside their profile. The store handles its own
        // listener teardown if bind() is called again (e.g. SuperAdmin
        // tenant impersonation re-points accountId), so we can call it
        // unconditionally on every login without leaking subscriptions.
        const permissionsStore = usePermissionsStore()
        permissionsStore.bind()

        // 🔔 Bind the notifications store. Listens to
        // `accounts/{accountId}/notifications` for new account
        // messages, cost-anomaly alerts, and long-running-query
        // alerts. Same lifecycle contract as permissionsStore — the
        // bind() call is idempotent for the same (accountId, uid)
        // tuple, so an account-impersonation re-bind doesn't leak
        // listeners.
        const notificationsStore = useNotificationsStore()
        notificationsStore.bind(accountId, uid)

        // 💬 We deliberately do NOT call initializeSnowflakeQueries here.
        //
        // initializeSnowflakeQueries → useSnowflake → snowflakeAuth (the
        // store function). snowflakeAuth has a hard-logout side effect:
        // if it can't validate or refresh the cookie's tokens, it pushes
        // the user to /logout (stores/snowflakeAuth.ts ~line 46). On a
        // FRESH OAuth signup, the credentials are populated milliseconds
        // before this listener runs, and any timing edge case
        // (browser/server clock skew, partially-set cookie, the cookie
        // was dropped due to size and only localStorage has the data
        // until the StatusIndicator restores it) causes a spurious
        // logout — which is what was kicking users from /register's
        // submit straight to /login mid-handshake.
        //
        // The StatusIndicator chip in the header (components/layout/
        // StatusIndicator.vue) probes the session on mount + every 60s
        // and writes the live role / warehouse / etc. into the globals
        // store via setSnowflakeSession. That's the single canonical
        // path for "what's the user's live Snowflake context?" — no
        // other component needs initializeSnowflakeQueries' side
        // effects, and the StatusIndicator handles its own auth-mode
        // detection (browser OAuth vs backend probe via user_snowflake_session)
        // without the logout-on-failure footgun.
    }

    // nuxtApp.vueApp.provide('auth', auth)
    // nuxtApp.provide('auth', auth)

    // nuxtApp.vueApp.provide('firestore', firestore)
    // nuxtApp.provide('firestore', firestore)

    // nuxtApp.vueApp.provide('cloudFunctions', functions)
    // nuxtApp.provide('cloudFunctions', functions)
    return {
        provide: {
            auth,
            firestore,
            cloudFunctions: functions,
            user,
            authHydrated,
        },
    }
})
